/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useState, useCallback} from 'react';
import {useAccessToken} from '@salesforce/commerce-sdk-react';

/**
 * Google Geocoding (GET /server/geocode) and Geolocation (POST /server/geolocate) with SLAS JWT (GG-009).
 *
 * @returns {Object} Geocoding methods and state
 */
export function useGeocoding() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const {getTokenWhenReady} = useAccessToken();

  /**
   * Build query string from parameters
   *
   * @param {Object} params - Query parameters
   * @returns {string} Query string
   */
  const buildQueryString = useCallback((params) => {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, value);
      }
    });

    return query.toString();
  }, []);

  /**
   * Generic geocoding request
   *
   * @param {Object} params - Geocoding parameters
   * @param {string} params.address - The street address or plus code to geocode
   * @param {string} params.latlng - Latitude and longitude values (for reverse geocoding)
   * @param {string} params.place_id - A place ID from Google Places API
   * @param {string} params.result_type - Filter results by type
   * @param {string} params.location_type - Filter results by location type
   * @param {string} params.language - Language for results
   * @param {string} params.region - Region code for biasing results
   * @param {string} params.bounds - Bounding box for biasing results
   * @param {string} params.components - Component filters (e.g., country:AU)
   * @returns {Promise<Object>} Geocoding results
   */
  const geocode = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      // Get SLAS access token for authentication (GG-009)
      const accessToken = await getTokenWhenReady();

      const queryString = buildQueryString(params);
      const response = await fetch(`/server/geocode?${queryString}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check for API errors
      if (data.error) {
        throw new Error(data.error_message || data.error);
      }

      setResults(data);
      setLoading(false);

      return data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to geocode';

      setError(errorMessage);
      setLoading(false);
      throw new Error(errorMessage);
    }
  }, [buildQueryString, getTokenWhenReady]);

  /**
   * Google Geolocation API via POST /server/geolocate (SLAS Bearer required).
   *
   * Success shape matches king-living `geolocate` middleware: coordinates from Geolocation API,
   * optional `accuracy`, and ISO country from server-side reverse geocode (may be null).
   *
   * @param {Object} [body] — forwarded keys only (e.g. considerIp, wifiAccessPoints)
   * @returns {Promise<{location: {lat: number, lng: number}, accuracy?: number, countryCode: string|null, countryName: string|null}>}
   */
  const geolocate = useCallback(async (body = {}) => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const accessToken = await getTokenWhenReady();
      const response = await fetch('/server/geolocate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error_message || data.message || data.error || `HTTP error! status: ${response.status}`
        );
      }

      const loc = data?.location;
      if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') {
        throw new Error('Invalid geolocate response: missing location coordinates');
      }

      const normalized = {
        location: {lat: loc.lat, lng: loc.lng},
        ...(typeof data.accuracy === 'number' ? {accuracy: data.accuracy} : {}),
        countryCode: data.countryCode ?? null,
        countryName: data.countryName ?? null,
      };

      setResults(normalized);
      setLoading(false);

      return normalized;
    } catch (err) {
      const errorMessage = err.message || 'Failed to geolocate';

      setError(errorMessage);
      setLoading(false);
      throw new Error(errorMessage);
    }
  }, [getTokenWhenReady]);

  /**
   * Geocode an address (forward geocoding)
   *
   * @param {string} address - The street address to geocode
   * @param {Object} options - Additional geocoding options
   * @param {string} options.language - Language for results (e.g., 'en', 'es')
   * @param {string} options.region - Region code for biasing (e.g., 'AU', 'US')
   * @param {string} options.bounds - Bounding box (e.g., 'lat1,lng1|lat2,lng2')
   * @param {string} options.components - Component filters (e.g., 'country:AU')
   * @returns {Promise<Object>} Geocoding results with coordinates
   */
  const geocodeAddress = useCallback(async (address, options = {}) => {
    return geocode({
      address,
      ...options,
    });
  }, [geocode]);

  /**
   * Reverse geocode coordinates (coordinates to address)
   *
   * @param {number} latitude - Latitude coordinate
   * @param {number} longitude - Longitude coordinate
   * @param {Object} options - Additional reverse geocoding options
   * @param {string} options.result_type - Filter by result type
   * @param {string} options.location_type - Filter by location type
   * @param {string} options.language - Language for results
   * @returns {Promise<Object>} Address information
   */
  const reverseGeocode = useCallback(async (latitude, longitude, options = {}) => {
    return geocode({
      latlng: `${latitude},${longitude}`,
      ...options,
    });
  }, [geocode]);

  /**
   * Lookup a place by place_id
   *
   * @param {string} placeId - Google Places API place_id
   * @param {Object} options - Additional options
   * @param {string} options.language - Language for results
   * @returns {Promise<Object>} Place details
   */
  const lookupPlaceById = useCallback(async (placeId, options = {}) => {
    return geocode({
      place_id: placeId,
      ...options,
    });
  }, [geocode]);

  /**
   * Search for addresses with component filters
   *
   * @param {string} address - Address to search
   * @param {string} country - Country code (e.g., 'AU', 'US')
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Filtered geocoding results
   */
  const geocodeWithCountry = useCallback(async (address, country, options = {}) => {
    return geocode({
      address,
      components: `country:${country}`,
      ...options,
    });
  }, [geocode]);

  /**
   * Get coordinates from the first result
   *
   * @param {Object} geocodeResults - Results from geocoding
   * @returns {Object|null} {lat, lng} or null if no results
   */
  const extractCoordinates = useCallback((geocodeResults) => {
    if (!geocodeResults?.results || geocodeResults.results.length === 0) {
      return null;
    }

    const {location} = geocodeResults.results[0].geometry;

    return {
      lat: location.lat,
      lng: location.lng,
    };
  }, []);

  /**
   * Get formatted address from the first result
   *
   * @param {Object} geocodeResults - Results from geocoding
   * @returns {string|null} Formatted address or null if no results
   */
  const extractFormattedAddress = useCallback((geocodeResults) => {
    if (!geocodeResults?.results || geocodeResults.results.length === 0) {
      return null;
    }

    return geocodeResults.results[0].formatted_address;
  }, []);

  /**
   * Extract specific address components
   *
   * @param {Object} geocodeResults - Results from geocoding
   * @param {string} componentType - Type of component (e.g., 'locality', 'postal_code')
   * @returns {string|null} Component value or null if not found
   */
  const extractAddressComponent = useCallback((geocodeResults, componentType) => {
    if (!geocodeResults?.results || geocodeResults.results.length === 0) {
      return null;
    }

    const addressComponents = geocodeResults.results[0].address_components;
    const component = addressComponents.find((comp) => comp.types.includes(componentType));

    return component ? component.long_name : null;
  }, []);

  /**
   * Clear results and error state
   */
  const clearResults = useCallback(() => {
    setResults(null);
    setError(null);
  }, []);

  return {
    // State
    loading,
    error,
    results,

    // Core methods
    geocode,
    geolocate,
    geocodeAddress,
    reverseGeocode,
    lookupPlaceById,
    geocodeWithCountry,

    // Utility methods
    extractCoordinates,
    extractFormattedAddress,
    extractAddressComponent,
    clearResults,
  };
}
